// Imports otimizados removendo componentes não utilizados
import React, { useState, useEffect } from 'react';
import { Typography, Card, Table, Input, Tabs, Timeline, Row, Col, Select, Tag, Button, Spin, Empty, message } from 'antd';
import { SearchOutlined, FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
import { Fornecedor, TimelineData } from '../../../../types/dashboard/fornecedores.types';
import FornecedoresService from '../../../../services/dashboard/fornecedores.service';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { GlobalContext } from '../../../../context/globalContext';
import QuestionarioModal from './QuestionarioModal';
import RelatorioFiltrosModal from '../../../RelatorioFiltrosModal';
import PDFGenerator from '../../../../services/pdfGenerator';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

interface FornecedorDetalhesProps {
  fornecedorId: string;
  onClose: () => void;
  onFornecedorChange?: (newFornecedorId: string) => void;
}

const FornecedorDetalhes: React.FC<FornecedorDetalhesProps> = ({ 
  fornecedorId, 
  onClose,
  onFornecedorChange 
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [fornecedor, setFornecedor] = useState<Fornecedor | null>(null);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [planosAcao, setPlanosAcao] = useState<any>(null);
  const [maturidade, setMaturidade] = useState<any>(null);
  const [maturidadeMedia, setMaturidadeMedia] = useState<any>(null);
  const [timelineItems, setTimelineItems] = useState<any[]>([]);
  const [questionariosData, setQuestionariosData] = useState<any[]>([]);
  const [buscaQuestionario, setBuscaQuestionario] = useState<string>('');
  const [selectedFornecedor, setSelectedFornecedor] = useState<string | null>(fornecedorId || null);
  
  // Estado para armazenar informações detalhadas do cliente
  const [clienteInfo, setClienteInfo] = useState<any>(null);

  // Função para abrir a modal com o questionário
  const abrirQuestionario = (record: any) => {
    setQuestionarioSelecionado(record);
    setModalVisible(true);
    setCarregandoRespostas(true);
    
    // Buscar as respostas específicas deste questionário
    fetchRespostasQuestionario(record.questionarioId);
  };
  
  // Estado para dados da timeline
  const [timelineData, setTimelineData] = useState<TimelineData>({
    inicioAcao: null,
    cadastroFornecedor: null,
    primeiroCadastro: null,
    primeiroEnvio: null,
    primeiraResposta: null,
    planoAcao: null,
    aplicacoesQuestionarios: [],
    disparosEmails: [],
    respostasQuestionarios: [],
    planosAplicados: [],
    planosRespondidos: [],
    liberacoesModulos: [],
    prazos: []
  });

  // Estados para armazenar os dados dos questionários
  const [questoesData, setQuestoesData] = useState<any[]>([]);
  const [loadingQuestionarios, setLoadingQuestionarios] = useState<boolean>(false);

  // Estados para armazenar dados da modal ver questionários
  const [modalVisible, setModalVisible] = useState(false);
  const [questionarioSelecionado, setQuestionarioSelecionado] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [carregandoRespostas, setCarregandoRespostas] = useState(false);

  // Estados para gerar relatórios
  const [relatorioModalVisible, setRelatorioModalVisible] = useState<boolean>(false);
  const [gerando, setGerando] = useState<boolean>(false);

  // Função para abrir o modal de relatório
  const abrirRelatorioModal = () => {
    // Garantir que temos os dados do cliente antes de abrir o modal
    carregarDadosCliente();
    setRelatorioModalVisible(true);
  };
  
  // Função para fechar o modal de relatório
  const fecharRelatorioModal = () => {
    setRelatorioModalVisible(false);
  };
  
  // Obter o contexto global
  const globalContext = React.useContext(GlobalContext);
  const currentCliente = globalContext?.currentCliente;

  // Função para carregar dados detalhados do cliente
  const carregarDadosCliente = async () => {
    try {
      // Primeiro tenta obter os dados do contexto global
      if (currentCliente && currentCliente._id) {
        const dadosCompletos = { ...currentCliente };
        setClienteInfo(dadosCompletos);
        return;
      }
      
      // Se não tiver no contexto, tenta buscar do localStorage
      const clienteId = getClienteIdFromStorage();
      if (!clienteId) {
        return;
      }
      
      // Tente buscar mais informações do localStorage
      try {
        const clienteStorage = localStorage.getItem('currentCliente');
        if (clienteStorage) {
          const clienteData = JSON.parse(clienteStorage);
          if (clienteData) {
            setClienteInfo(clienteData);
            return;
          }
        }
      } catch (error) {
        // Silenciar erro
      }
      
      // Como último recurso, tentar buscar da API de clientes
      try {
        // Supondo que exista um endpoint para buscar detalhes do cliente
        const response = await fetch(`/api/clientes/${clienteId}`);
        if (response.ok) {
          const clienteData = await response.json();
          setClienteInfo(clienteData);
        }
      } catch (apiError) {
        // Silenciar erro
      }
    } catch (error) {
      // Silenciar erro
    }
  };

  // Função para buscar as respostas do questionário específico
  const fetchRespostasQuestionario = async (questionarioId: string) => {
    try {
      // Usar respostas já carregadas se disponíveis, ou fazer uma nova requisição
      const clienteId = currentCliente?._id || getClienteIdFromStorage();
      
      if (!clienteId) {
        message.error('ID do cliente não disponível');
        setCarregandoRespostas(false);
        return;
      }
      
      // Filtrar as respostas para este questionário específico
      let respostasData = [];
      
      // Verificar se já temos as respostas em cache
      if (questionariosData.length > 0) {
        const questionario = questionariosData.find(q => q._id === questionarioId);
        if (questionario && questionario.respostas) {
          respostasData = questionario.respostas;
        }
      }
      
      // Se não encontrou em cache, buscar do servidor
      if (respostasData.length === 0) {
        try {
          respostasData = await FornecedoresService.getRespostasQuestionarios(questionarioId, clienteId);
        } catch (error) {
          // Silenciar erro
        }
      }
      
      // Processar e formatar as respostas para exibição
      const respostasFormatadas = respostasData.map((resposta: any) => {
        // Buscar a questão correspondente para mostrar o texto
        const questao = questoesData.find((q: any) => q._id === resposta.questao_id);
        
        return {
          pergunta: questao?.texto || 'Pergunta não encontrada',
          resposta: resposta.resposta || '',
          observacao: resposta.observacao || ''
        };
      });
      
      setRespostas(respostasFormatadas);
    } catch (error) {
      setRespostas([]);
    } finally {
      setCarregandoRespostas(false);
    }
  };

  // Buscar maturidade média de todos os fornecedores
  const fetchMaturidadeMedia = async (clienteId: string) => {
    try {
      const maturidadeData = await FornecedoresService.getMaturidade(clienteId);
      setMaturidadeMedia(maturidadeData);
    } catch (error) {
      setMaturidadeMedia({
        percentualMaturidade: 0,
        nivelConformidade: 'baixo'
      });
    }
  };

  // Buscar maturidade do fornecedor específico
  const fetchMaturidadeFornecedor = async (clienteId: string, fornecedorId: string) => {
    try {
      // Verificar se existem questionários respondidos para este fornecedor
      const respostasData = await FornecedoresService.getRespostasByFornecedor(fornecedorId, clienteId)
        .catch(() => []);
      
      // Se não tem respostas, maturidade é zero
      if (!respostasData || respostasData.length === 0) {
        setMaturidade({
          percentualMaturidade: 0,
          nivelConformidade: 'baixo'
        });
        return;
      }
      
      // Se tem respostas, calcular a maturidade baseada nas respostas
      try {
        // Obter a maturidade do fornecedor
        const maturidadeData = await FornecedoresService.getMaturidade(clienteId, fornecedorId);
        
        // Verificar se a resposta contém dados válidos
        if (maturidadeData && typeof maturidadeData.percentualMaturidade === 'number') {
          // Se o percentual é zero mas existem respostas, algo está errado
          if (maturidadeData.percentualMaturidade === 0 && respostasData.length > 0) {
            // Tentar calcular a maturidade manualmente
            // Este é um cálculo simplificado - ajuste conforme sua lógica de negócios
            const totalRespostas = respostasData.length;
            const respostasPositivas = respostasData.filter((r: { resposta: string; }) => 
              r.resposta === 'Sim' || 
              r.resposta === 'true' || 
              r.resposta === '1'
            ).length;
            
            const percentualCalculado = Math.round((respostasPositivas / totalRespostas) * 100);
            
            // Determinar nível de conformidade
            let nivelConformidade = 'baixo';
            if (percentualCalculado >= 75) nivelConformidade = 'alto';
            else if (percentualCalculado >= 40) nivelConformidade = 'medio';
            
            setMaturidade({
              percentualMaturidade: percentualCalculado,
              nivelConformidade: nivelConformidade as 'alto' | 'medio' | 'baixo'
            });
          } else {
            // Usar a maturidade da API
            setMaturidade(maturidadeData);
          }
        } else {
          // Resposta inválida
          setMaturidade({
            percentualMaturidade: 0,
            nivelConformidade: 'baixo'
          });
        }
      } catch (error) {
        setMaturidade({
          percentualMaturidade: 0,
          nivelConformidade: 'baixo'
        });
      }
    } catch (error) {
      setMaturidade({
        percentualMaturidade: 0,
        nivelConformidade: 'baixo'
      });
    }
  };

  // Carregar dados do fornecedor e outros dados relacionados
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Tentar carregar dados do cliente desde o início
        await carregarDadosCliente();
        
        if (!fornecedorId) {
          message.error('ID do fornecedor não disponível');
          setLoading(false);
          return;
        }
        
        // Verificar se temos o cliente_id
        if (!currentCliente || !currentCliente._id) {
          // Tentar obter o cliente_id do localStorage como fallback
          const clienteId = getClienteIdFromStorage();
          if (!clienteId) {
            message.error('ID do cliente não disponível');
            setLoading(false);
            return;
          }
          
          try {
            const timelineDataResponse = await FornecedoresService.getTimelineData(clienteId, fornecedorId);
            setTimelineData(timelineDataResponse);
            
            // Transformar os dados da timeline em itens para o componente Timeline
            setTimelineItems(gerarTimelineItems());
          } catch (error) {
            // Em caso de erro, fornecer um objeto válido
            setTimelineData({
              inicioAcao: null,
              cadastroFornecedor: null,
              primeiroCadastro: null,
              primeiroEnvio: null,
              primeiraResposta: null,
              planoAcao: null,
              aplicacoesQuestionarios: [],
              disparosEmails: [],
              respostasQuestionarios: [],
              planosAplicados: [],
              planosRespondidos: [],
              liberacoesModulos: [],
              prazos: []
            });
          }
    
          // Buscar dados dos questionários
          await fetchQuestionariosData(fornecedorId, clienteId);
          
          // Buscar maturidade média e do fornecedor
          await Promise.all([
            fetchMaturidadeMedia(clienteId),
            fetchMaturidadeFornecedor(clienteId, fornecedorId)
          ]);
          
          return;
        }
        
        // Carregar os dados em paralelo com o cliente_id do contexto
        const [
          fornecedorData, 
          planosData, 
          maturidadeFornecedor,
          maturidadeGeral,
          fornecedoresLista,
          timelineDataResponse
        ] = await Promise.all([
          FornecedoresService.getFornecedor(fornecedorId, currentCliente._id),
          FornecedoresService.getPlanosAcao(fornecedorId, currentCliente._id),
          FornecedoresService.getMaturidade(currentCliente._id, fornecedorId),
          FornecedoresService.getMaturidade(currentCliente._id),
          FornecedoresService.getFornecedores({
            page: 1,
            limit: 100,
            cliente_id: currentCliente._id
          }),
          FornecedoresService.getTimelineData(currentCliente._id, fornecedorId)
        ]);
        
        // Atualizar estados com os dados obtidos
        setFornecedor(fornecedorData);
        setPlanosAcao(planosData);
        setMaturidade(maturidadeFornecedor);
        setMaturidadeMedia(maturidadeGeral);
        setTimelineData(timelineDataResponse);
        
        // Verificar se a resposta contém dados no formato esperado
        const listaFornecedores = Array.isArray(fornecedoresLista?.data) 
          ? fornecedoresLista.data 
          : Array.isArray(fornecedoresLista) 
            ? fornecedoresLista 
            : [];
            
        setFornecedores(listaFornecedores);
        
        // Buscar dados dos questionários
        await fetchQuestionariosData(fornecedorId, currentCliente._id);
        
        // Gerar timeline com dados dinâmicos
        setTimelineItems(gerarTimelineItems());
        
        // Atualizar o ID do fornecedor selecionado
        setSelectedFornecedor(fornecedorId);
        
      } catch (error) {
        message.error('Falha ao carregar dados do fornecedor');
      } finally {
        setLoading(false);
      }
    };
    
    if (fornecedorId) {
      fetchData();
    }
  }, [fornecedorId, currentCliente]);

  // Função para obter cliente_id do localStorage como fallback
  const getClienteIdFromStorage = () => {
    try {
      // Tenta obter do localStorage
      const userInfoString = localStorage.getItem('userInfo');
      if (userInfoString) {
        const userInfo = JSON.parse(userInfoString);
        if (userInfo?.cliente_id) return userInfo.cliente_id;
      }
      
      const currentClienteString = localStorage.getItem('currentCliente');
      if (currentClienteString) {
        const currentCliente = JSON.parse(currentClienteString);
        if (currentCliente?._id) return currentCliente._id;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };
  
  // Função para gerar itens de timeline baseados no fornecedor
  const gerarTimelineItems = () => {
    const items = [];
    
    // Formatar data no padrão DD/MM/YYYY HH:MM para exibição
    const formatarDataExibicao = (dataStr: string | number | Date) => {
      if (!dataStr) return '';
      try {
        const data = new Date(dataStr);
        
        const dia = data.getDate().toString().padStart(2, '0');
        const mes = (data.getMonth() + 1).toString().padStart(2, '0');
        const ano = data.getFullYear();
        const horas = data.getHours().toString().padStart(2, '0');
        const minutos = data.getMinutes().toString().padStart(2, '0');
        
        return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
      } catch (error) {
        return '';
      }
    };
    
    // Converter string de data para timestamp para permitir ordenação correta
    const obterTimestamp = (dataStr: string | number | Date) => {
      if (!dataStr) return 0;
      try {
        return new Date(dataStr).getTime();
      } catch (error) {
        return 0;
      }
    };

    // 1. Liberação de módulo - Início da ação
    if (timelineData.inicioAcao) {
      items.push({
        color: 'blue',
        dot: <div style={{ backgroundColor: '#1890FF', width: 10, height: 10, borderRadius: '50%' }} />,
        children: (
          <>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
              {formatarDataExibicao(timelineData.inicioAcao)}
            </p>
            <p style={{ margin: 0 }}>
              <Text strong>Liberação de Módulo</Text> - Início da ação
            </p>
          </>
        ),
        timestamp: obterTimestamp(timelineData.inicioAcao) // Adicionar timestamp para ordenação
      });
    }

    // 2. Cadastro de fornecedor
    if (timelineData.cadastroFornecedor) {
      items.push({
        color: 'gray',
        dot: <div style={{ backgroundColor: '#d9d9d9', width: 10, height: 10, borderRadius: '50%' }} />,
        children: (
          <>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
              {formatarDataExibicao(timelineData.cadastroFornecedor)}
            </p>
            <p style={{ margin: 0 }}>
              <Text strong>Cadastro</Text> - Fornecedor registrado no sistema
            </p>
          </>
        ),
        timestamp: obterTimestamp(timelineData.cadastroFornecedor) // Adicionar timestamp
      });
    }

    // 3. Aplicações de questionários
    if (timelineData.aplicacoesQuestionarios && timelineData.aplicacoesQuestionarios.length > 0) {
      timelineData.aplicacoesQuestionarios.forEach((aplicacao, index) => {
        items.push({
          color: 'green',
          dot: <div style={{ backgroundColor: '#52C41A', width: 10, height: 10, borderRadius: '50%' }} />,
          children: (
            <>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                {formatarDataExibicao(aplicacao.data)}
              </p>
              <p style={{ margin: 0 }}>
                <Text strong>Aplicação de Questionário</Text> - {aplicacao.titulo || `Questionário ${index + 1}`}
              </p>
            </>
          ),
          timestamp: obterTimestamp(aplicacao.data) // Adicionar timestamp
        });
      });
    }

    // 4. Disparos de e-mails
    if (timelineData.disparosEmails && timelineData.disparosEmails.length > 0) {
      timelineData.disparosEmails.forEach((email) => {
        items.push({
          color: 'geekblue',
          dot: <div style={{ backgroundColor: '#2f54eb', width: 10, height: 10, borderRadius: '50%' }} />,
          children: (
            <>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                {formatarDataExibicao(email.data)}
              </p>
              <p style={{ margin: 0 }}>
                <Text strong>Disparo de E-mail</Text> - {email.assunto || 'Comunicação enviada'}
              </p>
            </>
          ),
          timestamp: obterTimestamp(email.data) // Adicionar timestamp
        });
      });
    }

    // 5. Respostas de questionários
    if (timelineData.respostasQuestionarios && timelineData.respostasQuestionarios.length > 0) {
      timelineData.respostasQuestionarios.forEach((resposta, index) => {
        items.push({
          color: 'orange',
          dot: <div style={{ backgroundColor: '#fa8c16', width: 10, height: 10, borderRadius: '50%' }} />,
          children: (
            <>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                {formatarDataExibicao(resposta.data)}
              </p>
              <p style={{ margin: 0 }}>
                <Text strong>Questionário Respondido</Text> - {resposta.titulo || `Questionário ${index + 1}`}
              </p>
            </>
          ),
          timestamp: obterTimestamp(resposta.data) // Adicionar timestamp
        });
      });
    }

    // 6. Planos de ação aplicados
    if (timelineData.planosAplicados && timelineData.planosAplicados.length > 0) {
      timelineData.planosAplicados.forEach((plano) => {
        items.push({
          color: 'purple',
          dot: <div style={{ backgroundColor: '#722ed1', width: 10, height: 10, borderRadius: '50%' }} />,
          children: (
            <>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                {formatarDataExibicao(plano.data)}
              </p>
              <p style={{ margin: 0 }}>
                <Text strong>Plano de Ação Aplicado</Text> - {plano.titulo || 'Plano de ação'}
              </p>
            </>
          ),
          timestamp: obterTimestamp(plano.data) // Adicionar timestamp
        });
      });
    }

    // 7. Planos de ação respondidos
    if (timelineData.planosRespondidos && timelineData.planosRespondidos.length > 0) {
      timelineData.planosRespondidos.forEach((plano) => {
        items.push({
          color: 'magenta',
          dot: <div style={{ backgroundColor: '#eb2f96', width: 10, height: 10, borderRadius: '50%' }} />,
          children: (
            <>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                {formatarDataExibicao(plano.data)}
              </p>
              <p style={{ margin: 0 }}>
                <Text strong>Plano de Ação Respondido</Text> - {plano.titulo || 'Resposta ao plano'}
                {plano.status && <Tag color="blue" style={{ marginLeft: 8 }}>{plano.status}</Tag>}
              </p>
            </>
          ),
          timestamp: obterTimestamp(plano.data) // Adicionar timestamp
        });
      });
    }

    // 8. Liberações de módulos
    if (timelineData.liberacoesModulos && timelineData.liberacoesModulos.length > 0) {
      timelineData.liberacoesModulos.forEach((liberacao) => {
        items.push({
          color: 'cyan',
          dot: <div style={{ backgroundColor: '#13c2c2', width: 10, height: 10, borderRadius: '50%' }} />,
          children: (
            <>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                {formatarDataExibicao(liberacao.data)}
              </p>
              <p style={{ margin: 0 }}>
                <Text strong>Liberação de Módulo</Text> - {liberacao.nome || 'Novo módulo'}
              </p>
            </>
          ),
          timestamp: obterTimestamp(liberacao.data) // Adicionar timestamp
        });
      });
    }

    // 9. Prazos
    if (timelineData.prazos && timelineData.prazos.length > 0) {
      timelineData.prazos.forEach((prazo, index) => {
        items.push({
          color: 'red',
          dot: <div style={{ backgroundColor: '#f5222d', width: 10, height: 10, borderRadius: '50%' }} />,
          children: (
            <>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                {formatarDataExibicao(prazo.data_prazo)}
              </p>
              <p style={{ margin: 0 }}>
                <Text strong>Prazo</Text> - {prazo.descricao || `Prazo ${index + 1}`}
              </p>
            </>
          ),
          timestamp: obterTimestamp(prazo.data_prazo) // Adicionar timestamp
        });
      });
    }

    // Ordenar por timestamp (data numérica) em vez de string
    return items.sort((a, b) => a.timestamp - b.timestamp);
  };
  
  // Função para buscar dados de questionários
  const fetchQuestionariosData = async (fornecedorId: string, clienteId: string) => {
    try {
      setLoadingQuestionarios(true);
      
      // Tentar buscar os questionários
      let questionariosResponse = [];
      try {
        questionariosResponse = await FornecedoresService.getRespostasByFornecedor(fornecedorId, clienteId);
      } catch (error: any) {
        // Se recebemos um 404, assumimos que não há questionários para este fornecedor ainda
        if (error.response && error.response.status === 404) {
          questionariosResponse = []; // Definir como array vazio em vez de falhar
        } else {
          // Para outros erros, propagamos para ser tratado no catch externo
          throw error;
        }
      }
      
      // Verificar e normalizar a resposta dos questionários
      if (questionariosResponse && !Array.isArray(questionariosResponse)) {
        if (Object.keys(questionariosResponse).length > 0) {
          // Se recebemos um objeto único, transformamos em array
          questionariosResponse = [questionariosResponse];
        } else {
          // Se recebemos um objeto vazio, transformamos em array vazio
          questionariosResponse = [];
        }
      }
      
      // Buscar as questões (independente se temos questionários ou não)
      let questoesResponse = [];
      try {
        questoesResponse = await FornecedoresService.getQuestoes();
      } catch (error) {
        questoesResponse = [];
      }
      
      // Armazenar os dados
      setQuestionariosData(questionariosResponse);
      setQuestoesData(questoesResponse);
      
    } catch (error) {
      message.error('Não foi possível carregar os dados dos questionários');
      
      // Definir estados como arrays vazios em caso de erro
      setQuestionariosData([]);
      setQuestoesData([]);
    } finally {
      setLoadingQuestionarios(false);
    }
  };

  // Função para formatar as respostas dos questionários em um formato amigável para a tabela
  const formatQuestionariosForTable = () => {
    if (!questionariosData || questionariosData.length === 0) {
      return [];
    }
    
    return questionariosData.map((questionario, index) => {
      // Verificar se questionario é undefined ou não possui propriedades necessárias
      if (!questionario || typeof questionario !== 'object') {
        return {
          key: `invalid-${index}`,
          questionarioId: `invalid-${index}`,
          razaoSocial: `Questionário inválido ${index + 1}`,
          cnpj: fornecedor?.cnpj || '-',
          email: fornecedor?.email || '-',
          area: fornecedor?.area || '-',
          ultimoQuestionario: 'Erro',
          dataEnvio: '-',
          respostasCount: 0
        };
      }
      
      // Formato para cada questionário
      return {
        key: questionario._id || `q-${index}`,
        questionarioId: questionario._id || `q-${index}`,
        razaoSocial: questionario.titulo || `Questionário ${index + 1}`,
        cnpj: questionario.cnpj || fornecedor?.cnpj || '-',
        email: questionario.email || fornecedor?.email || '-',
        area: questionario.area || fornecedor?.area || '-',
        ultimoQuestionario: questionario.status || 'Pendente',
        dataEnvio: questionario.created_at ? new Date(questionario.created_at).toLocaleDateString() : '-',
        respostasCount: questionario.respostas ? questionario.respostas.length : 0
      };
    });
  };
  
  // Função para filtrar questionários
 const filtrarQuestionarios = () => {
   const questionariosFormatados = formatQuestionariosForTable();
   
   if (!buscaQuestionario) return questionariosFormatados;
   
   return questionariosFormatados.filter(q => 
     q.razaoSocial.toLowerCase().includes(buscaQuestionario.toLowerCase()) || 
     q.cnpj.includes(buscaQuestionario) ||
     (q.email && q.email.toLowerCase().includes(buscaQuestionario.toLowerCase()))
   );
 };  
 
 // Determina a cor do termômetro com base no nível de conformidade
 const getThermometerColor = (nivelConformidade: string) => {
   switch (nivelConformidade) {
     case 'alto':
       return '#52c41a'; // Verde
     case 'medio':
       return '#faad14'; // Amarelo
     case 'baixo':
       return '#ff4d4f'; // Vermelho
     default:
       return '#7B39DB'; // Roxo como padrão
   }
 };
 
 // Traduzir nível de conformidade
 const traduzirNivelConformidade = (nivel: string) => {
   switch (nivel) {
     case 'alto': return 'ALTA';
     case 'medio': return 'MÉDIA';
     case 'baixo': return 'BAIXA';
     default: return 'N/A';
   }
 };

 const getThermometerGradient = (nivelConformidade: string) => {
   switch (nivelConformidade) {
     case 'alto':
       return `linear-gradient(to top, #cf1322 0%, #faad14 50%, #389e0d 100%)`;
     case 'medio':
       return `linear-gradient(to top, #cf1322 0%, #faad14 50%, #ffc53d 100%)`;
     case 'baixo':
       return `linear-gradient(to top, #cf1322 0%, #ff4d4f 50%, #ff7875 100%)`;
     default:
       return `linear-gradient(to top, #cf1322 0%, #faad14 50%, #389e0d 100%)`;
   }
 };

 // Nova função para obter dados completos do cliente para o relatório
 const obterDadosClienteCompletos = () => {
   // Se temos informações detalhadas do cliente, usar primeiro
   if (clienteInfo) {
     return {
       nome: clienteInfo.razao_social || clienteInfo.nome || obterNomeCliente(),
       cnpj: clienteInfo.cnpj || '',
       email: clienteInfo.email || '',
       telefone: clienteInfo.telefone || '',
       endereco: clienteInfo.endereco || '',
       setor: clienteInfo.setor || clienteInfo.industria || ''
     };
   }
   
   // Se não temos clienteInfo detalhado, montar com o que temos
   if (currentCliente) {
     return {
       nome: currentCliente.razao_social || currentCliente.nome || obterNomeCliente(),
       cnpj: currentCliente.cnpj || '',
       email: currentCliente.email || '',
       telefone: currentCliente.telefone || '',
       endereco: currentCliente.endereco || '',
       setor: currentCliente.setor || currentCliente.industria || ''
     };
   }
   
   // Criar objeto mínimo com o que temos disponível
   return {
     nome: obterNomeCliente(),
     cnpj: '',
     email: '',
     telefone: ''
   };
 };

 // Se estiver carregando, mostra um spinner
 if (loading) {
   return (
     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
       <Spin size="large" />
     </div>
   );
 }
 
 // Se não encontrou o fornecedor, mostra mensagem de erro
 if (!fornecedor) {
   return (
     <Empty 
       description="Fornecedor não encontrado" 
       style={{ margin: '50px 0' }}
     >
       <Button type="primary" onClick={onClose}>Voltar</Button>
     </Empty>
   );
 }

 // Função para obter o próximo prazo do fornecedor
 const obterProximoPrazo = () => {
   if (!timelineData.prazos || timelineData.prazos.length === 0) {
     return '-';
   }
   
   // Obter a data atual
   const hoje = new Date();
   
   // Filtrar prazos futuros
   const prazosFuturos = timelineData.prazos
     .filter(prazo => {
       const dataPrazo = new Date(prazo.data_prazo);
       return dataPrazo >= hoje;
     })
     .sort((a, b) => {
       return new Date(a.data_prazo).getTime() - new Date(b.data_prazo).getTime();
     });
   
   // Se não houver prazos futuros, pegar o mais recente
   if (prazosFuturos.length === 0) {
     const ultimoPrazo = timelineData.prazos
       .sort((a, b) => {
         return new Date(b.data_prazo).getTime() - new Date(a.data_prazo).getTime();
       })[0];
     
     // Formatar a data no padrão brasileiro (DD/MM/YYYY)
     const data = new Date(ultimoPrazo.data_prazo);
     const dia = data.getDate().toString().padStart(2, '0');
     const mes = (data.getMonth() + 1).toString().padStart(2, '0');
     const ano = data.getFullYear();
     
     return `${dia}/${mes}/${ano} (expirado)`;
   }
   
   // Retornar o próximo prazo futuro
   const proximoPrazo = prazosFuturos[0];
   
   // Formatar a data no padrão brasileiro (DD/MM/YYYY)
   const data = new Date(proximoPrazo.data_prazo);
   const dia = data.getDate().toString().padStart(2, '0');
   const mes = (data.getMonth() + 1).toString().padStart(2, '0');
   const ano = data.getFullYear();
   
   return `${dia}/${mes}/${ano}${proximoPrazo.descricao ? ` - ${proximoPrazo.descricao}` : ''}`;
 };

 // Atualiza o array fornecedorTableData para incluir o próximo prazo
 const fornecedorTableData = fornecedor ? [
   {
     id: fornecedor.id,
     razaoSocial: fornecedor.razaoSocial || 'Nome não disponível',
     representante: fornecedor.representante || '-',
     cnpj: fornecedor.cnpj || '-',
     email: fornecedor.email || '-',
     telefone: fornecedor.telefone || '-',
     ultimoEnvio: fornecedor.ultimoQuestionario?.status || '-',
     proximoPrazo: obterProximoPrazo(),
     status: fornecedor.status || 'Vigente'
   }
 ] : [];

 // Função para lidar com mudanças de fornecedores
 const handleFornecedorChange = async (value: string) => {
   // Validar se o valor é válido
   if (!value || value === 'Todos os Fornecedores') {
     message.info('Por favor, selecione um fornecedor específico');
     return;
   }
 
   setSelectedFornecedor(value);
   
   if (onFornecedorChange) {
     onFornecedorChange(value);
   } else {
     // Alternar para atualização de URL com API de histórico em vez de recarregar a página inteira
     if (window.history && window.history.pushState) {
       const newUrl = `/fornecedores/${value}`;
       window.history.pushState({ fornecedorId: value }, '', newUrl);
       
       setLoading(true);
       
       try {
         // Buscar dados do fornecedor diretamente
         const clienteId = currentCliente?._id || getClienteIdFromStorage();
         
         if (!clienteId) {
           message.error('ID do cliente não disponível');
           setLoading(false);
           return;
         }
         
         // Verificar primeiro se o fornecedor existe
         let fornecedorExiste = false;
         
         // Verificar na lista de fornecedores já carregada
         if (fornecedores && fornecedores.length > 0) {
           fornecedorExiste = fornecedores.some(f => f.id === value);
         }
         
         // Se não encontrou na lista local, tentar buscar diretamente
         if (!fornecedorExiste) {
           try {
             // Verificar se o fornecedor existe antes de carregar outros dados
             const checkFornecedor = await FornecedoresService.getFornecedor(value, clienteId);
             
             if (!checkFornecedor) {
               throw new Error('Fornecedor não encontrado');
             }
             
             fornecedorExiste = true;
           } catch (error) {
             message.error('Fornecedor não encontrado ou indisponível');
             setLoading(false);
             return;
           }
         }
         
         // Se chegou aqui, o fornecedor existe. Carregar os dados em paralelo.
         try {
           const [
             fornecedorData, 
             planosData, 
             maturidadeFornecedor,
             maturidadeGeral,
             timelineDataResponse
           ] = await Promise.all([
             FornecedoresService.getFornecedor(value, clienteId),
             FornecedoresService.getPlanosAcao(value, clienteId).catch(error => {
               return null; // Retornar null em caso de erro para não interromper outras solicitações
             }),
             FornecedoresService.getMaturidade(clienteId, value).catch(error => {
               return { percentualMaturidade: 0, nivelConformidade: 'baixo' };
             }),
             FornecedoresService.getMaturidade(clienteId).catch(error => {
               return { percentualMaturidade: 0, nivelConformidade: 'baixo' };
             }),
             FornecedoresService.getTimelineData(clienteId, value).catch(error => {
               return {
                 inicioAcao: null,
                 cadastroFornecedor: null,
                 primeiroCadastro: null,
                 primeiroEnvio: null,
                 primeiraResposta: null,
                 planoAcao: null,
                 aplicacoesQuestionarios: [],
                 disparosEmails: [],
                 respostasQuestionarios: [],
                 planosAplicados: [],
                 planosRespondidos: [],
                 liberacoesModulos: [],
                 prazos: []
               };
             })
           ]);
           
           // Atualizar dados apenas se o fornecedor foi encontrado
           if (fornecedorData) {
             // Atualizar estados com os dados obtidos
             setFornecedor(fornecedorData);
             setPlanosAcao(planosData);
             setMaturidade(maturidadeFornecedor);
             setMaturidadeMedia(maturidadeGeral);
             setTimelineData(timelineDataResponse);
             
             // Buscar questionários para o novo fornecedor
             await fetchQuestionariosData(value, clienteId);
             
             // Atualizar timeline com dados dinâmicos
             setTimelineItems(gerarTimelineItems());
           } else {
             message.error('Não foi possível carregar os dados do fornecedor');
           }
         } catch (error) {
           message.error('Falha ao carregar dados do fornecedor. Por favor, tente novamente.');
         }
       } catch (error) {
         message.error('Ocorreu um erro ao mudar de fornecedor');
       } finally {
         setLoading(false);
       }
     }
   }
 };

 // Função para gerar o relatório com os filtros selecionados
 const gerarRelatorio = async (filtros: { tipoVisualizacao: string; fornecedorId: string; search: any; status: any; }) => {
   try {
     setGerando(true);
     
     // Obter o ID do cliente
     const clienteId = currentCliente?._id || getClienteIdFromStorage() || '';
     
     if (!clienteId) {
       message.error('ID do cliente não disponível');
       return;
     }
     
     // Para visualização detalhada (Informações Gerais), usar o ID do fornecedor atual se não foi especificado
     if (filtros.tipoVisualizacao === 'detalhada' && !filtros.fornecedorId) {
       filtros.fornecedorId = fornecedorId; // usar o ID do fornecedor atual
     }
     
     // Adicionar o nome do cliente aos filtros
     const nomeCliente = obterNomeCliente();
     
     // Adicionar informações completas do cliente aos filtros
     const dadosClienteCompletos = obterDadosClienteCompletos();
     
     // Escolher o tipo de relatório com base na visualização selecionada
     const tipoRelatorio = filtros.tipoVisualizacao === 'consolidada' ? 'dashboard' : 'fornecedor';
     
     // Preparar parâmetros para o PDFGenerator
     const params = {
       clienteId,
       fornecedorId: filtros.fornecedorId || fornecedorId,
       filtros: {
         ...filtros,
         search: filtros.search || buscaQuestionario,
         status: filtros.status || (fornecedor?.status ? [fornecedor.status] : []),
         nomeCliente: nomeCliente, // Nome do cliente
         clienteInfo: dadosClienteCompletos // Dados completos do cliente para o relatório
       }
     };
     
     // Usar o método gerarEVisualizar para exibir em uma nova aba do navegador
     await PDFGenerator.gerarEVisualizar(tipoRelatorio, params);
     
     message.success(`Relatório ${tipoRelatorio === 'dashboard' ? 'consolidado' : 'detalhado'} gerado com sucesso!`);
   } catch (error) {
     message.error('Falha ao gerar o relatório. Tente novamente.');
   } finally {
     setGerando(false);
   }
 };

 // Função para obter o nome do cliente de todas as fontes possíveis
 const obterNomeCliente = () => {
   // Se temos informações detalhadas do cliente, usar primeiro
   if (clienteInfo) {
     if (clienteInfo.razao_social) return clienteInfo.razao_social;
     if (clienteInfo.nome) return clienteInfo.nome;
   }
   
   // Verificar razao_social primeiro (que parece ser o campo usado no sistema)
   if (currentCliente?.razao_social) {
     return currentCliente.razao_social;
   }
   
   // Verificar nome como fallback
   if (currentCliente?.nome) {
     return currentCliente.nome;
   }
   
   // Tentar obter do localStorage
   try {
     const currentClienteString = localStorage.getItem('currentCliente');
     if (currentClienteString) {
       const clienteData = JSON.parse(currentClienteString);
       if (clienteData?.razao_social) {
         return clienteData.razao_social;
       }
       if (clienteData?.nome) {
         return clienteData.nome;
       }
     }
   } catch (error) {
   }
   
   // Verificar variável global com tipagem segura
   try {
     // @ts-ignore - Ignorar erro de tipagem para acessar propriedade customizada do window
     if (typeof window !== 'undefined' && window.currentClienteName) {
       // @ts-ignore
       return window.currentClienteName;
     }
   } catch (error) {
     // Silenciar erro
   }
   
   return "Relatório de Conformidade"; // Valor default melhorado
 };

 // Renderização do componente
 return (
   <div>
     {/* Header com controle de visualização e seleção de fornecedor */}
     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
       <div style={{ display: 'flex', alignItems: 'center' }}>
         <Text style={{ marginRight: 8, fontSize: 16 }}>Selecionar Fornecedor: </Text>
         <Select
           style={{ width: 300 }}
           placeholder="Todos os Fornecedores"
           onChange={handleFornecedorChange}
           value={selectedFornecedor || fornecedorId}
         >
           <Option key="todos" value="Todos os Fornecedores">Todos os Fornecedores</Option>
           {fornecedores.map(f => (
             <Option key={f.id} value={f.id}>{f.razaoSocial}</Option>
           ))}
         </Select>
       </div>
     </div>
     
     {/* Título da seção */}
     <Text style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>Informações do fornecedor</Text>
     
     {/* Tabela de dados básicos do fornecedor */}
     <Table 
       pagination={false} 
       showHeader={true}
       style={{ 
         marginBottom: 24,
         boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
         backgroundColor: '#f8faff'
       }}
       className="fornecedor-info-table"
       columns={[
         { 
           title: 'Razão Social', 
           dataIndex: 'razaoSocial', 
           key: 'razaoSocial',
           render: text => <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{text}</span>
         },
         { 
           title: 'Nome do responsável', 
           dataIndex: 'representante', 
           key: 'responsavel',
           render: text => <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{text}</span>
         },
         { 
           title: 'CNPJ', 
           dataIndex: 'cnpj', 
           key: 'cnpj',
           render: text => <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{text}</span>
         },
         { 
           title: 'Email', 
           dataIndex: 'email', 
           key: 'email',
           render: text => <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{text}</span>
         },
         { 
           title: 'Telefone', 
           dataIndex: 'telefone', 
           key: 'telefone',
           render: text => <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{text}</span>
         },
         { 
           title: 'Próximo Prazo', 
           dataIndex: 'proximoPrazo', 
           key: 'proximoPrazo',
           render: text => {
             // Verificar se contém "expirado"
             const isExpirado = text.includes('expirado');
             return (
               <span style={{ 
                 fontWeight: 'bold', 
                 fontSize: '15px',
                 color: isExpirado ? '#ff4d4f' : '#1890ff'
               }}>
                 {text}
               </span>
             );
           }
         },
         { 
           title: 'Último envio', 
           dataIndex: 'ultimoEnvio', 
           key: 'ultimoEnvio',
           render: text => <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{text}</span>
         },
         { 
           title: 'Status', 
           dataIndex: 'status', 
           key: 'status', 
           render: (status) => (
             <div>
               <span style={{ 
                 display: 'inline-block', 
                 width: 8, 
                 height: 8, 
                 backgroundColor: status === 'Vigente' ? '#52C41A' : '#ff4d4f', 
                 borderRadius: '50%',
                 marginRight: 6 
               }} />
               <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{status}</span>
             </div>
           )
         }
       ]}
       dataSource={fornecedorTableData}
     />

     {/* Três colunas com gráficos */}
     <Row gutter={16}>
       {/* Coluna 1 - Timeline */}
       <Col span={8}>
         <Card title="Linha do Tempo da Ação" style={{ height: 'auto', minHeight: '500px', border: '2px solid black' }}>
           {loading ? (
             <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
               <Spin />
             </div>
           ) : timelineItems.length > 0 ? (
             <div>
               <Timeline items={timelineItems} />
             </div>
           ) : (
             <Empty description="Nenhum evento de timeline disponível" />
           )}
         </Card>
       </Col>
       
       {/* Coluna 2 - Planos de ação */}
       <Col span={8}>
         <Card 
           title="Planos de Ação" 
           style={{ height: '500px', border: '2px solid black' }} 
         >
           {planosAcao ? (
             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: 280 }}>
               {/* Calcular os dados do gráfico com três cores */}
               {(() => {
                 const percentualConcluido = planosAcao.percentualConcluido || 0;
                 
                 const percentualLiberados = Math.min(
                   Math.round((planosAcao.planosIniciados / (planosAcao.totalFornecedores || 1)) * 100),
                   percentualConcluido
                 );
                 
                 const percentualVazio = Math.max(0, 100 - percentualConcluido);
                 
                 const chartData = [
                   { name: 'Concluído', value: percentualConcluido, color: '#7B39DB' }, // Roxo
                   { name: 'Liberados', value: percentualLiberados, color: '#39DBDB' }, // Azul turquesa
                   { name: 'Vazio', value: percentualVazio, color: '#E0E0E0' } // Cinza claro
                 ].filter(item => item.value > 0); // Remover itens com valor zero
                 
                 return (
                   <>
                     {/* Legenda e valores para CIMA do gráfico */}
                     <div style={{ width: '100%', marginBottom: 30 }}>
                       <Row gutter={[16, 0]} justify="center">
                         <Col>
                           <div style={{ 
                             display: 'flex', 
                             alignItems: 'center', 
                             backgroundColor: '#f5f5f5', 
                             padding: '8px 12px', 
                             borderRadius: '4px',
                             boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                           }}>
                             <div 
                               style={{ 
                                 width: 12, 
                                 height: 12, 
                                 backgroundColor: '#7B39DB', 
                                 borderRadius: '50%',
                                 marginRight: 8 
                               }}
                             />
                             <div>
                               {/* Informações do Card */}
                               <Text strong style={{ fontSize: '14px' }}>Questionários respondidos</Text>
                               <div><Text strong>{planosAcao.respostasObtidas}</Text> / {planosAcao.totalFornecedores || 0}</div>
                             </div>
                           </div>
                         </Col>
                         <Col>
                           <div style={{ 
                             display: 'flex', 
                             alignItems: 'center', 
                             backgroundColor: '#f5f5f5', 
                             padding: '8px 12px', 
                             borderRadius: '4px',
                             boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                           }}>
                             <div 
                               style={{ 
                                 width: 12, 
                                 height: 12, 
                                 backgroundColor: '#39DBDB', 
                                 borderRadius: '50%',
                                 marginRight: 8 
                               }}
                             />
                             <div>
                               {/* Informações do Card */}
                               <Text strong style={{ fontSize: '14px' }}>Planos liberados</Text>
                               <div><Text strong>{planosAcao.planosIniciados}</Text></div>
                             </div>
                           </div>
                         </Col>
                       </Row>
                     </div>
                     
                     {/* Gráfico de progresso circular */}
                     <div style={{ position: 'relative', width: '100%', height: 200, marginTop: 10 }}>
                       <ResponsiveContainer width="100%" height="100%">
                         <PieChart>
                           <Pie
                             data={chartData}
                             cx="50%"
                             cy="50%"
                             innerRadius={60}
                             outerRadius={80}
                             startAngle={90}
                             endAngle={-270}
                             paddingAngle={0}
                             dataKey="value"
                           >
                             {chartData.map((entry, index) => (
                               <Cell key={`cell-${index}`} fill={entry.color} />
                             ))}
                           </Pie>
                         </PieChart>
                       </ResponsiveContainer>
                       
                       {/* Porcentagem no centro */}
                       <div 
                         style={{ 
                           position: 'absolute', 
                           top: '50%', 
                           left: '50%', 
                           transform: 'translate(-50%, -50%)',
                           textAlign: 'center'
                         }}
                       >
                         <Title level={2} style={{ margin: 0, color: '#7B39DB' }}>
                           {percentualConcluido}%
                         </Title>
                         <Text type="secondary">Concluído</Text>
                       </div>
                     </div>
                   </>
                 );
               })()}
             </div>
           ) : (
             <Empty description="Dados não disponíveis" />
           )}
         </Card>
       </Col>
       
       {/* Coluna 3 - Maturidade com altura fixa */}
       <Col span={8}>
         <Card 
           title="Maturidade" 
           style={{ height: '500px', border: '2px solid black' }}
         >
           {maturidade ? (
             <div style={{ display: 'flex', height: 'calc(100% - 40px)', justifyContent: 'center', position: 'relative' }}>
               {/* Container para os termômetros com altura fixa */}
               <div style={{ display: 'flex', height: 250, position: 'relative', width: '100%' }}>
                 {/* Primeiro Termômetro - Fornecedor */}
                 <div style={{ width: '45%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                   {/* Escala do termômetro */}
                   <div style={{ position: 'absolute', left: 10, top: 0, bottom: 30, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                     <Text>100%</Text>
                     <Text>75%</Text>
                     <Text>50%</Text>
                     <Text>25%</Text>
                     <Text>0%</Text>
                   </div>
                   
                   {/* Termômetro base */}
                   <div style={{ 
                     width: 50, 
                     height: '100%', 
                     backgroundColor: '#f0f0f0', 
                     borderRadius: 20,
                     position: 'relative',
                     overflow: 'visible', // Mudado para 'visible' para mostrar as setas
                     margin: '0 auto',
                     left: 10
                   }}>
                     {/* Fundo do termômetro com gradiente baseado no nível */}
                     <div style={{
                       position: 'absolute',
                       bottom: 0,
                       left: 0,
                       right: 0,
                       height: '100%',
                       background: getThermometerGradient(maturidade.nivelConformidade),
                       borderRadius: '20px 20px 20px 20px',
                       opacity: 0.2
                     }} />
                     
                     {/* Preenchimento do termômetro */}
                     <div style={{ 
                       position: 'absolute',
                       bottom: 0,
                       left: 0,
                       right: 0,
                       height: `${maturidade.percentualMaturidade}%`,
                       background: getThermometerGradient(maturidade.nivelConformidade),
                       borderRadius: '0 0 20px 20px',
                       transition: 'height 1s ease-in-out'
                     }} />
                     
                     {/* Marcador com setas laterais pretas apontando para dentro */}
                     <div style={{
                       position: 'absolute',
                       bottom: `${maturidade.percentualMaturidade}%`,
                       left: -15, // Aumentado para dar mais espaço
                       right: -15, // Aumentado para dar mais espaço
                       height: 6, // Aumentado para acomodar setas maiores
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       zIndex: 5 // Aumentado o z-index
                     }}>
                       {/* Seta esquerda apontando para dentro (direita) */}
                       <div style={{
                         width: 0,
                         height: 0,
                         borderTop: '8px solid transparent',
                         borderBottom: '8px solid transparent',
                         borderLeft: '12px solid #000000'
                       }} />
                       
                       {/* Barra central */}
                       <div style={{
                         width: 50, // Largura fixa em vez de flex
                         height: 3,
                         backgroundColor: '#000000',
                         margin: '0 2px'
                       }} />
                       
                       {/* Seta direita apontando para dentro (esquerda) */}
                       <div style={{
                         width: 0,
                         height: 0,
                         borderTop: '8px solid transparent',
                         borderBottom: '8px solid transparent',
                         borderRight: '12px solid #000000'
                       }} />
                     </div>
                     
                     {/* Texto do percentual - Dentro do termômetro */}
                     <div style={{
                       position: 'absolute',
                       bottom: `calc(${maturidade.percentualMaturidade}% / 1 - 30px)`,
                       left: '50%',
                       transform: 'translateX(-50%)',
                       zIndex: 4,
                       padding: '4px 8px',
                       whiteSpace: 'nowrap'
                     }}>
                       <Text style={{ 
                         fontSize: 14, 
                         fontWeight: 'bold',
                         color: 'white'
                       }}>
                         {maturidade.percentualMaturidade}%
                       </Text>
                     </div>
                   </div>
                 </div>
                 
                 {/* Segundo Termômetro - Média do Cliente */}
                 <div style={{ width: '45%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                   {/* Escala do termômetro */}
                   <div style={{ position: 'absolute', left: 40, top: 0, bottom: 30, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                     <Text>100%</Text>
                     <Text>75%</Text>
                     <Text>50%</Text>
                     <Text>25%</Text>
                     <Text>0%</Text>
                   </div>
                   
                   {/* Termômetro base */}
                   <div style={{ 
                     width: 50, 
                     height: '100%', 
                     backgroundColor: '#f0f0f0', 
                     borderRadius: 20,
                     position: 'relative',
                     overflow: 'visible', // Mudado para 'visible' para mostrar as setas
                     margin: '0 auto',
                     right: -35
                   }}>
                     {/* Fundo do termômetro com gradiente baseado no nível */}
                     <div style={{
                       position: 'absolute',
                       bottom: 0,
                       left: 0,
                       right: 0,
                       height: '100%',
                       background: getThermometerGradient(maturidadeMedia?.nivelConformidade || 'medio'),
                       borderRadius: '20px 20px 20px 20px',
                       opacity: 0.2
                     }} />
                     
                     {/* Preenchimento do termômetro */}
                     <div style={{ 
                       position: 'absolute',
                       bottom: 0,
                       left: 0,
                       right: 0,
                       height: `${maturidadeMedia?.percentualMaturidade || 0}%`,
                       background: getThermometerGradient(maturidadeMedia?.nivelConformidade || 'medio'),
                       borderRadius: '0 0 20px 20px',
                       transition: 'height 1s ease-in-out'
                     }} />
                     
                     {/* Marcador com setas laterais pretas apontando para dentro */}
                     <div style={{
                       position: 'absolute',
                       bottom: `${maturidadeMedia?.percentualMaturidade || 0}%`,
                       left: -15, // Aumentado para dar mais espaço
                       right: -15, // Aumentado para dar mais espaço
                       height: 6, // Aumentado para acomodar setas maiores
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       zIndex: 5 // Aumentado o z-index
                     }}>
                       {/* Seta esquerda apontando para dentro (direita) */}
                       <div style={{
                         width: 0,
                         height: 0,
                         borderTop: '8px solid transparent',
                         borderBottom: '8px solid transparent',
                         borderLeft: '12px solid #000000'
                       }} />
                       
                       {/* Barra central */}
                       <div style={{
                         width: 50, // Largura fixa em vez de flex
                         height: 3,
                         backgroundColor: '#000000',
                         margin: '0 2px'
                       }} />
                       
                       {/* Seta direita apontando para dentro (esquerda) */}
                       <div style={{
                         width: 0,
                         height: 0,
                         borderTop: '8px solid transparent',
                         borderBottom: '8px solid transparent',
                         borderRight: '12px solid #000000'
                       }} />
                     </div>
                     
                     {/* Texto do percentual - Dentro do termômetro */}
                     <div style={{
                       position: 'absolute',
                       bottom: `calc(${maturidadeMedia?.percentualMaturidade || 0}% / 1 - 30px)`,
                       left: '50%',
                       transform: 'translateX(-50%)',
                       zIndex: 4,
                       padding: '4px 8px',
                       whiteSpace: 'nowrap'
                     }}>
                       <Text style={{ 
                         fontSize: 14, 
                         fontWeight: 'bold',
                         color: 'white'
                       }}>
                         {maturidadeMedia?.percentualMaturidade || 0}%
                       </Text>
                     </div>
                   </div>
                 </div>
               </div>
               
               {/* Labels e badges com posição fixa */}
               <div style={{ position: 'absolute', bottom: -80, left: 0, right: 0, display: 'flex', justifyContent: 'space-around' }}>
                 {/* Label do fornecedor */}
                 <div style={{ textAlign: 'center', width: '45%' }}>
                   <Text style={{ fontWeight: 'bold' }}>Fornecedor</Text>
                   <br></br>
                   <Text style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                     Percentual médio do fornecedor selecionado
                   </Text>
                 </div>
                 
                 {/* Label da média geral */}
                 <div style={{ textAlign: 'center', width: '45%' }}>
                   <Text style={{ fontWeight: 'bold' }}>Média do Cliente (Consolidada)</Text>
                   <br></br>
                 </div>
               </div>
               
               {/* Rótulos e informações abaixo dos termômetros */}
               <div style={{ position: 'absolute', bottom: -130, left: 0, right: 0, display: 'flex', justifyContent: 'space-around' }}>
                 {/* Nível de maturidade do fornecedor */}
                 <div style={{
                   backgroundColor: getThermometerColor(maturidade.nivelConformidade),
                   color: 'white',
                   fontWeight: 'bold',
                   padding: '8px 16px',
                   borderRadius: 4,
                   textAlign: 'center',
                   width: '40%'
                 }}>
                   {traduzirNivelConformidade(maturidade.nivelConformidade)}
                 </div>
                 
                 {/* Nível de maturidade da média geral */}
                 <div style={{
                   backgroundColor: getThermometerColor(maturidadeMedia?.nivelConformidade || 'baixo'),
                   color: 'white',
                   fontWeight: 'bold',
                   padding: '8px 16px',
                   borderRadius: 4,
                   textAlign: 'center',
                   width: '40%'
                 }}>
                   {traduzirNivelConformidade(maturidadeMedia?.nivelConformidade || 'baixo')}
                 </div>
               </div>
             </div>
           ) : (
             <Empty description="Dados não disponíveis" />
           )}
         </Card>
       </Col>
     </Row>
     
     {/* Tabs na parte inferior */}
     <div style={{ marginTop: 24 }}>
       <Tabs defaultActiveKey="vigentes">
         <TabPane tab={`Questionários (${questionariosData.length})`} key="questionarios" />
       </Tabs>
       
       {/* Campo de busca */}
       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
         <Input 
           placeholder="Buscar questionários" 
           style={{ width: 300 }} 
           suffix={<SearchOutlined />}
           value={buscaQuestionario}
           onChange={(e) => setBuscaQuestionario(e.target.value)}  
         />
         <div>
           <Button
             type="primary" 
             icon={<FilePdfOutlined />}
             onClick={abrirRelatorioModal}
             loading={gerando}
           >
             Gerar Relatório
           </Button>
         </div>
       </div>
       
       {/* Tabela de questionários */}
       <Table 
         columns={[
           { title: 'Questionário', dataIndex: 'razaoSocial', key: 'razaoSocial' },
           { title: 'CNPJ', dataIndex: 'cnpj', key: 'cnpj' },
           { title: 'Email', dataIndex: 'email', key: 'email' },
           { title: 'Área', dataIndex: 'area', key: 'area' },
           { title: 'Status', dataIndex: 'ultimoQuestionario', key: 'ultimoQuestionario', render: (status) => (
             <Tag color={status === 'Pendente' ? 'gold' : status === 'Enviado' ? 'green' : 'blue'}>
               <span style={{ 
                 display: 'inline-block', 
                 width: 8, 
                 height: 8, 
                 backgroundColor: status === 'Pendente' ? '#FFC107' : status === 'Enviado' ? '#52C41A' : '#1890FF', 
                 borderRadius: '50%',
                 marginRight: 6 
               }} />
               {status}
             </Tag>
           )},
           { title: 'Data de Envio', dataIndex: 'dataEnvio', key: 'dataEnvio' },
           { title: 'Respostas', dataIndex: 'respostasCount', key: 'respostasCount' },
           { title: 'Ações', key: 'acoes', render: (_, record) => (
             <Button
               type="primary" 
               size="small" 
               icon={<EyeOutlined />}
               onClick={() => abrirQuestionario(record)}
             >
               Visualizar
             </Button>
           )},
         ]}
         dataSource={filtrarQuestionarios()} // Função para filtrar os questionários
         pagination={{ pageSize: 5 }}  // Paginação para melhor usabilidade
         rowKey="key"
         loading={loadingQuestionarios}
         style={{ backgroundColor: '#fff', borderRadius: 8 }}
         locale={{ emptyText: 'Nenhum questionário encontrado' }}
       />
     </div>
     
     {/* Abrir QuestionarioModal */}
     <QuestionarioModal
       visible={modalVisible}
       onClose={() => setModalVisible(false)}
       questionario={questionarioSelecionado}
       respostas={respostas}
       carregando={carregandoRespostas}
     />

     {/* Relatório Modal */}
     <RelatorioFiltrosModal
       visible={relatorioModalVisible}
       onCancel={fecharRelatorioModal}
       onGenerate={gerarRelatorio}
       clienteId={currentCliente?._id || getClienteIdFromStorage() || ''}
       clienteNome={obterNomeCliente()}
       fornecedoresList={fornecedores}
       filtrosAtuais={{
         search: buscaQuestionario,
         status: fornecedor?.status ? [fornecedor.status] : []
       }}
       tipo="fornecedor"
       fornecedorId={selectedFornecedor || fornecedorId}
       fornecedorPredefinido={selectedFornecedor || fornecedorId}
     />
   </div>
 );
};

export default FornecedorDetalhes;